import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import blogsData from '../content/data/blogs.json';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaUser, FaTag } from 'react-icons/fa';

const BlogPageContainer = styled.div`
  background-color: var(--background-off-white);
  padding: 4rem 0;
`;

const Container = styled.div`
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
`;

const PageSubtitle = styled.p`
  font-size: 1.25rem;
  color: var(--text-light);
  max-width: 700px;
  margin: 0 auto;
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
`;

const BlogCard = styled(motion.div)`
  background: white;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-md);
  transition: all var(--transition-normal);
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    box-shadow: var(--shadow-xl);
    transform: translateY(-5px);
  }
`;

const BlogImage = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.5rem;
  height: 250px;
  position: relative;
  overflow: hidden;
  background-color: var(--primary-color);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: all var(--transition-slow);
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    opacity: 0.2;
  }

  &:hover img {
    transform: scale(1.05);
    opacity: 0.3;
  }
  
  h3 {
    color: white;
    z-index: 2;
    font-size: 1.5rem;
    line-height: 1.3;
    text-shadow: 0 2px 5px rgba(0,0,0,0.5);
  }
`;

const CategoryTag = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: var(--accent-color);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-xs);
  font-weight: 600;
  text-transform: uppercase;
  z-index: 2;
`;

const BlogContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: white;
`;

const BlogTitle = styled.h3`
  font-size: var(--font-size-xl);
  font-weight: 700;
  margin-bottom: 0.75rem;
  line-height: 1.4;

  a {
    color: var(--text-dark);
    text-decoration: none;

    &:hover {
      color: var(--primary-color);
    }
  }
`;

const BlogExcerpt = styled.p`
  color: var(--text-medium);
  font-size: var(--font-size-base);
  margin-bottom: 1.5rem;
  flex-grow: 1;
`;

const BlogMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: var(--font-size-sm);
  color: var(--text-light);
  border-top: 1px solid var(--border-color);
  padding-top: 1rem;
  margin-top: auto;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;

  svg {
    margin-right: 0.5rem;
  }
`;

const FilterButton = styled.button`
  background: ${props => (props.active ? 'var(--primary-color)' : 'white')};
  color: ${props => (props.active ? 'white' : 'var(--text-dark)')};
  border: 1px solid
    ${props => (props.active ? 'var(--primary-color)' : 'var(--border-color)')};
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-normal);
  font-weight: 500;

  &:hover {
    background: var(--primary-light);
    color: white;
    border-color: var(--primary-light);
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3rem;
`;

const PageButton = styled.button`
  padding: 0.5rem 1rem;
  margin: 0 0.25rem;
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--border-color);
  background: ${props => (props.active ? 'var(--primary-color)' : 'white')};
  color: ${props => (props.active ? 'white' : 'var(--text-dark)')};
  cursor: pointer;
  transition: all var(--transition-normal);
  font-weight: 500;

  &:hover {
    background: var(--primary-light);
    color: white;
    border-color: var(--primary-light);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Blog = () => {
  const [filter, setFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  const categories = useMemo(() => {
    const allCategories = blogsData.map(post => post.category);
    return ['All', ...new Set(allCategories)];
  }, []);

  const filteredPosts = useMemo(() => {
    if (filter === 'All') {
      return blogsData;
    }
    return blogsData.filter(post => post.category === filter);
  }, [filter]);

  const paginatedPosts = useMemo(() => {
    const startIndex = (currentPage - 1) * postsPerPage;
    return filteredPosts.slice(startIndex, startIndex + postsPerPage);
  }, [filteredPosts, currentPage, postsPerPage]);

  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <BlogPageContainer>
      <Container>
        <Header>
          <PageTitle>US Energy Trading Insights</PageTitle>
          <PageSubtitle>
            Your source for the latest news, analysis, and expert commentary on US energy trading, CFTC compliance, and position limits monitoring.
          </PageSubtitle>
        </Header>

        <FilterContainer>
          {categories.map(category => (
            <FilterButton
              key={category}
              active={filter === category}
              onClick={() => {
                setFilter(category);
                setCurrentPage(1);
              }}
            >
              {category}
            </FilterButton>
          ))}
        </FilterContainer>

        <BlogGrid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {paginatedPosts.map(post => (
            <BlogCard key={post.id} variants={itemVariants}>
              <BlogImage to={`/blog/${post.id}`}>
                <img src={post.image} alt={post.title} />
                <h3>{post.title}</h3>
                <CategoryTag>{post.category}</CategoryTag>
              </BlogImage>
              <BlogContent>
                <BlogTitle>
                  <Link to={`/blog/${post.id}`}>{post.title}</Link>
                </BlogTitle>
                <BlogExcerpt>{post.excerpt}</BlogExcerpt>
                <BlogMeta>
                  <MetaItem>
                    <FaCalendarAlt /> {post.date}
                  </MetaItem>
                  <MetaItem>
                    <FaUser /> {post.author}
                  </MetaItem>
                </BlogMeta>
              </BlogContent>
            </BlogCard>
          ))}
        </BlogGrid>

        {totalPages > 1 && (
          <Pagination>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <PageButton
                key={page}
                active={currentPage === page}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </PageButton>
            ))}
          </Pagination>
        )}
      </Container>
    </BlogPageContainer>
  );
};

export default Blog; 